import React, {
    createContext,
    useContext,
    useEffect,
    useRef,
    useState,
    useCallback,
    useMemo,
} from 'react';
import termService from '@/shared/services/termService';
import { useAuth } from '@/shared/contexts/AuthContext';
import { isEmailVerified } from '@/shared/utils/emailVerified';

const TermContext = createContext();

export function useTerm() {
    const context = useContext(TermContext);
    if (!context) {
        throw new Error('useTerm must be used within a TermProvider');
    }
    return context;
}

export function TermProvider({ children }) {
    const { currentUser, loading: authLoading } = useAuth();
    const [currentTerm, setCurrentTerm] = useState(null);
    const [termsLoading, setTermsLoading] = useState(true);
    const [termsError, setTermsError] = useState(null);
    // Keyed on the uid rather than the user object: Firebase hands back a new
    // object on every token refresh, and a refetch per refresh is a request
    // for a term that has not changed.
    const uid = currentUser?.uid || null;
    // The endpoint refuses an unverified password session, and verifying an
    // address keeps the same uid. Keyed on uid alone, a new account that
    // signed in unverified, then verified in the same tab, would never ask
    // again. So verification is part of the key, the same rule AuthContext
    // applies before it loads a profile.
    const verified = isEmailVerified(currentUser);
    // Identifies the newest request. A response to one that was superseded,
    // or that lands after sign-out cleared the term, would otherwise write a
    // term back into a state that should have none.
    const requestIdRef = useRef(0);

    const fetchTerm = useCallback(async () => {
        const requestId = requestIdRef.current + 1;
        requestIdRef.current = requestId;
        try {
            setTermsLoading(true);
            setTermsError(null);
            const term = await termService.getCurrentTerm();
            if (requestIdRef.current !== requestId) return;
            setCurrentTerm(term);
        } catch (error) {
            if (requestIdRef.current !== requestId) return;
            console.error('Error fetching current term:', error);
            setTermsError('Failed to load current term.');
            setCurrentTerm(null);
        } finally {
            if (requestIdRef.current === requestId) setTermsLoading(false);
        }
    }, []);

    // The current-term endpoint requires sign-in. Fetching on mount alone
    // meant the request went out before Firebase had restored the session, or
    // on the login page with nobody signed in; it answered 401, and nothing
    // asked again after sign-in, so the whole session ran with no term and
    // every screen keyed on it (the course catalog, the start panel) showed
    // nothing. So this waits for auth to settle, and asks again whenever the
    // signed-in user changes.
    useEffect(() => {
        // Defensive: AuthProvider renders its children only once auth has
        // settled, so in the running app this provider never sees
        // authLoading true. Kept so the provider stays correct if it is ever
        // mounted outside that gate.
        if (authLoading) return;
        if (!uid || !verified) {
            // Signed out, or not yet verified: there is no term to load,
            // rather than a load that failed, so this is not an error. Moving
            // the request id on discards any fetch still in flight.
            requestIdRef.current += 1;
            setCurrentTerm(null);
            setTermsError(null);
            setTermsLoading(false);
            return;
        }
        fetchTerm();
    }, [authLoading, uid, verified, fetchTerm]);

    const currentTermCode = currentTerm?.code || null;

    const refreshTerm = useCallback(() => {
        fetchTerm();
    }, [fetchTerm]);

    const value = useMemo(
        () => ({
            currentTerm,
            currentTermCode,
            termsLoading,
            termsError,
            refreshTerm,
        }),
        [currentTerm, currentTermCode, termsLoading, termsError, refreshTerm]
    );

    return (
        <TermContext.Provider value={value}>{children}</TermContext.Provider>
    );
}

export default TermContext;
